import React, { useState } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import {
  useContentCreator,
  BlogPost,
  SocialPost,
  EmailSequence,
  VideoScript,
} from "../../../../hooks/useContentCreator";

interface ContentCreatorModalProps {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
}

/**
 * Blog Post Tab Component
 */
const BlogPostTab: React.FC<{
  projectId: string;
  blogPosts: BlogPost[];
  isGenerating: boolean;
  onGenerate: (topic: string, keyword?: string, wordCount?: number) => void;
  onRemove: (index: number) => void;
}> = ({ blogPosts, isGenerating, onGenerate, onRemove }) => {
  const [topic, setTopic] = useState("");
  const [targetKeyword, setTargetKeyword] = useState("");
  const [wordCount, setWordCount] = useState(1500);
  const [expandedPost, setExpandedPost] = useState<number | null>(null);

  const handleGenerate = () => {
    if (topic.trim()) {
      onGenerate(topic, targetKeyword || undefined, wordCount);
      setTopic("");
      setTargetKeyword("");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatBlogPost = (post: BlogPost): string => {
    let content = `# ${post.title}\n\n`;
    content += `**Meta Description:** ${post.metaDescription}\n\n`;
    content += `**Target Keyword:** ${post.targetKeyword}\n`;
    content += `**Secondary Keywords:** ${post.secondaryKeywords.join(", ")}\n\n`;
    content += `---\n\n`;

    post.sections.forEach((section) => {
      content += `## ${section.heading}\n\n${section.content}\n\n`;
      if (section.subheadings) {
        section.subheadings.forEach((sub) => {
          content += `### ${sub.heading}\n\n${sub.content}\n\n`;
        });
      }
    });

    content += `## Conclusion\n\n${post.conclusion}\n\n`;
    content += `**Call to Action:** ${post.callToAction}\n`;

    return content;
  };

  return (
    <div className="p-4">
      {/* Generation Form */}
      <div className="card bg-light-primary border-0 mb-6">
        <div className="card-body">
          <h5 className="fw-bold mb-4">Generate Blog Post</h5>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">Topic or Title</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., How to validate your startup idea in 30 days"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Target Keyword (optional)</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., validate startup idea"
                value={targetKeyword}
                onChange={(e) => setTargetKeyword(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Word Count</label>
              <select
                className="form-select"
                value={wordCount}
                onChange={(e) => setWordCount(Number(e.target.value))}
              >
                <option value={800}>Short (~800 words)</option>
                <option value={1500}>Medium (~1500 words)</option>
                <option value={2500}>Long (~2500 words)</option>
                <option value={3500}>Comprehensive (~3500 words)</option>
              </select>
            </div>
            <div className="col-12">
              <button
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={isGenerating || !topic.trim()}
              >
                {isGenerating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-magic me-2" />
                    Generate Blog Post
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Generated Posts */}
      {blogPosts.length > 0 && (
        <div>
          <h5 className="fw-bold mb-4">Generated Blog Posts ({blogPosts.length})</h5>
          {blogPosts.map((post, index) => (
            <div key={index} className="card mb-4 border">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div
                  className="cursor-pointer flex-grow-1"
                  onClick={() => setExpandedPost(expandedPost === index ? null : index)}
                >
                  <h6 className="mb-0 fw-bold">{post.title}</h6>
                  <small className="text-muted">
                    {post.wordCount} words | {post.readingTime} read
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-light-primary"
                    onClick={() => copyToClipboard(formatBlogPost(post))}
                    title="Copy as Markdown"
                  >
                    <i className="bi bi-clipboard" />
                  </button>
                  <button
                    className="btn btn-sm btn-light-danger"
                    onClick={() => onRemove(index)}
                    title="Remove"
                  >
                    <i className="bi bi-trash" />
                  </button>
                </div>
              </div>
              {expandedPost === index && (
                <div className="card-body">
                  <div className="mb-4">
                    <span className="badge bg-primary me-2">{post.targetKeyword}</span>
                    {post.secondaryKeywords.map((kw, i) => (
                      <span key={i} className="badge bg-light-primary text-primary me-1">
                        {kw}
                      </span>
                    ))}
                  </div>
                  <p className="text-muted fst-italic mb-4">{post.metaDescription}</p>
                  <hr />
                  {post.sections.map((section, sIdx) => (
                    <div key={sIdx} className="mb-4">
                      <h5 className="fw-bold">{section.heading}</h5>
                      <p style={{ whiteSpace: "pre-wrap" }}>{section.content}</p>
                      {section.subheadings?.map((sub, subIdx) => (
                        <div key={subIdx} className="ms-4 mb-3">
                          <h6 className="fw-semibold">{sub.heading}</h6>
                          <p style={{ whiteSpace: "pre-wrap" }}>{sub.content}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                  <hr />
                  <h5 className="fw-bold">Conclusion</h5>
                  <p style={{ whiteSpace: "pre-wrap" }}>{post.conclusion}</p>
                  <div className="alert alert-primary mt-4">
                    <strong>Call to Action:</strong> {post.callToAction}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {blogPosts.length === 0 && !isGenerating && (
        <div className="text-center py-10 text-muted">
          <i className="bi bi-file-earmark-text fs-1 d-block mb-3" />
          <p>No blog posts generated yet. Enter a topic above to get started.</p>
        </div>
      )}
    </div>
  );
};

/**
 * Social Media Tab Component
 */
const SocialMediaTab: React.FC<{
  projectId: string;
  socialMedia: ReturnType<typeof useContentCreator>["socialMedia"];
  isGenerating: boolean;
  onGenerate: (
    topic: string,
    platforms: ("instagram" | "linkedin" | "twitter" | "tiktok" | "facebook")[],
    postsPerPlatform: number
  ) => void;
}> = ({ socialMedia, isGenerating, onGenerate }) => {
  const [topic, setTopic] = useState("");
  const [platforms, setPlatforms] = useState<
    ("instagram" | "linkedin" | "twitter" | "tiktok" | "facebook")[]
  >(["instagram", "linkedin", "twitter"]);
  const [postsPerPlatform, setPostsPerPlatform] = useState(3);
  const [activePlatform, setActivePlatform] = useState<string>("instagram");

  const handleGenerate = () => {
    if (topic.trim() && platforms.length > 0) {
      onGenerate(topic, platforms, postsPerPlatform);
    }
  };

  const togglePlatform = (
    platform: "instagram" | "linkedin" | "twitter" | "tiktok" | "facebook"
  ) => {
    setPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const platformIcons: Record<string, string> = {
    instagram: "bi-instagram",
    linkedin: "bi-linkedin",
    twitter: "bi-twitter-x",
    tiktok: "bi-tiktok",
    facebook: "bi-facebook",
  };

  const platformColors: Record<string, string> = {
    instagram: "bg-danger",
    linkedin: "bg-primary",
    twitter: "bg-dark",
    tiktok: "bg-dark",
    facebook: "bg-primary",
  };

  const renderPosts = (posts: SocialPost[]) => {
    if (!posts || posts.length === 0) return null;
    return posts.map((post, index) => (
      <div key={index} className="card mb-3 border">
        <div className="card-body">
          <div className="d-flex justify-content-between mb-2">
            <span className={`badge ${platformColors[post.platform]} text-white`}>
              <i className={`${platformIcons[post.platform]} me-1`} />
              {post.platform}
            </span>
            <span className="badge bg-light text-muted">{post.characterCount} chars</span>
          </div>
          <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
            {post.content}
          </p>
          <div className="mb-2">
            {post.hashtags.map((tag, i) => (
              <span key={i} className="badge bg-light-info text-info me-1">
                #{tag}
              </span>
            ))}
          </div>
          {post.imagePrompt && (
            <div className="alert alert-light py-2 mb-2">
              <small>
                <strong>Image idea:</strong> {post.imagePrompt}
              </small>
            </div>
          )}
          <button
            className="btn btn-sm btn-light-primary"
            onClick={() => copyToClipboard(`${post.content}\n\n${post.hashtags.map((t) => `#${t}`).join(" ")}`)}
          >
            <i className="bi bi-clipboard me-1" />
            Copy
          </button>
        </div>
      </div>
    ));
  };

  return (
    <div className="p-4">
      {/* Generation Form */}
      <div className="card bg-light-info border-0 mb-6">
        <div className="card-body">
          <h5 className="fw-bold mb-4">Generate Social Media Content</h5>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">Topic</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Launch announcement for our new product"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold">Platforms</label>
              <div className="d-flex flex-wrap gap-2">
                {(["instagram", "linkedin", "twitter", "tiktok", "facebook"] as const).map(
                  (platform) => (
                    <button
                      key={platform}
                      className={`btn btn-sm ${
                        platforms.includes(platform) ? "btn-primary" : "btn-light"
                      }`}
                      onClick={() => togglePlatform(platform)}
                    >
                      <i className={`${platformIcons[platform]} me-1`} />
                      {platform.charAt(0).toUpperCase() + platform.slice(1)}
                    </button>
                  )
                )}
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Posts per Platform</label>
              <select
                className="form-select"
                value={postsPerPlatform}
                onChange={(e) => setPostsPerPlatform(Number(e.target.value))}
              >
                <option value={1}>1 post</option>
                <option value={3}>3 posts</option>
                <option value={5}>5 posts</option>
              </select>
            </div>
            <div className="col-12">
              <button
                className="btn btn-info"
                onClick={handleGenerate}
                disabled={isGenerating || !topic.trim() || platforms.length === 0}
              >
                {isGenerating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-magic me-2" />
                    Generate Social Content
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Generated Content */}
      {socialMedia && (
        <div>
          <h5 className="fw-bold mb-4">Generated Social Media Content</h5>
          <ul className="nav nav-tabs mb-4">
            {Object.keys(platformIcons).map((platform) => {
              const posts = socialMedia[platform as keyof typeof socialMedia];
              if (!posts || (Array.isArray(posts) && posts.length === 0)) return null;
              return (
                <li key={platform} className="nav-item">
                  <button
                    className={`nav-link ${activePlatform === platform ? "active" : ""}`}
                    onClick={() => setActivePlatform(platform)}
                  >
                    <i className={`${platformIcons[platform]} me-1`} />
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </button>
                </li>
              );
            })}
          </ul>
          {renderPosts(socialMedia[activePlatform as keyof typeof socialMedia] as SocialPost[])}
        </div>
      )}

      {!socialMedia && !isGenerating && (
        <div className="text-center py-10 text-muted">
          <i className="bi bi-share fs-1 d-block mb-3" />
          <p>No social content generated yet. Enter a topic above to get started.</p>
        </div>
      )}
    </div>
  );
};

/**
 * Email Sequences Tab Component
 */
const EmailSequencesTab: React.FC<{
  projectId: string;
  emailSequences: EmailSequence[];
  isGenerating: boolean;
  onGenerate: (type: "welcome" | "nurture" | "launch" | "reengagement", count: number) => void;
  onRemove: (index: number) => void;
}> = ({ emailSequences, isGenerating, onGenerate, onRemove }) => {
  const [sequenceType, setSequenceType] = useState<"welcome" | "nurture" | "launch" | "reengagement">(
    "welcome"
  );
  const [emailCount, setEmailCount] = useState(5);
  const [expandedSequence, setExpandedSequence] = useState<number | null>(null);
  const [expandedEmail, setExpandedEmail] = useState<number | null>(null);

  const handleGenerate = () => {
    onGenerate(sequenceType, emailCount);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const sequenceTypeLabels: Record<string, { label: string; description: string }> = {
    welcome: { label: "Welcome Sequence", description: "Onboard new subscribers" },
    nurture: { label: "Nurture Sequence", description: "Build relationship over time" },
    launch: { label: "Launch Sequence", description: "Promote product launch with urgency" },
    reengagement: { label: "Re-engagement", description: "Win back inactive subscribers" },
  };

  return (
    <div className="p-4">
      {/* Generation Form */}
      <div className="card bg-light-success border-0 mb-6">
        <div className="card-body">
          <h5 className="fw-bold mb-4">Generate Email Sequence</h5>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Sequence Type</label>
              <select
                className="form-select"
                value={sequenceType}
                onChange={(e) =>
                  setSequenceType(e.target.value as "welcome" | "nurture" | "launch" | "reengagement")
                }
              >
                {Object.entries(sequenceTypeLabels).map(([value, { label, description }]) => (
                  <option key={value} value={value}>
                    {label} - {description}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Number of Emails</label>
              <select
                className="form-select"
                value={emailCount}
                onChange={(e) => setEmailCount(Number(e.target.value))}
              >
                <option value={3}>3 emails</option>
                <option value={5}>5 emails</option>
                <option value={7}>7 emails</option>
              </select>
            </div>
            <div className="col-12">
              <button
                className="btn btn-success"
                onClick={handleGenerate}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-magic me-2" />
                    Generate Email Sequence
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Generated Sequences */}
      {emailSequences.length > 0 && (
        <div>
          <h5 className="fw-bold mb-4">Generated Sequences ({emailSequences.length})</h5>
          {emailSequences.map((sequence, seqIndex) => (
            <div key={seqIndex} className="card mb-4 border">
              <div
                className="card-header d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => setExpandedSequence(expandedSequence === seqIndex ? null : seqIndex)}
              >
                <div>
                  <h6 className="mb-0 fw-bold">{sequence.name}</h6>
                  <small className="text-muted">
                    {sequenceTypeLabels[sequence.type]?.label} | {sequence.totalEmails} emails
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-light-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(seqIndex);
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                  <i
                    className={`bi ${expandedSequence === seqIndex ? "bi-chevron-up" : "bi-chevron-down"}`}
                  />
                </div>
              </div>
              {expandedSequence === seqIndex && (
                <div className="card-body">
                  <p className="text-muted mb-4">{sequence.description}</p>
                  <div className="mb-4">
                    <strong>Recommended Timing:</strong>
                    <ul className="mb-0 mt-2">
                      {sequence.recommendedTiming.map((timing, i) => (
                        <li key={i}>{timing}</li>
                      ))}
                    </ul>
                  </div>
                  <hr />
                  {sequence.emails.map((email, emailIndex) => (
                    <div key={emailIndex} className="mb-4">
                      <div
                        className="d-flex justify-content-between align-items-center cursor-pointer p-3 bg-light rounded"
                        onClick={() =>
                          setExpandedEmail(expandedEmail === emailIndex ? null : emailIndex)
                        }
                      >
                        <div>
                          <span className="badge bg-primary me-2">Day {email.sendDay}</span>
                          <strong>{email.subject}</strong>
                        </div>
                        <i
                          className={`bi ${
                            expandedEmail === emailIndex ? "bi-chevron-up" : "bi-chevron-down"
                          }`}
                        />
                      </div>
                      {expandedEmail === emailIndex && (
                        <div className="p-3 border rounded-bottom">
                          <div className="mb-3">
                            <strong>Subject Variants:</strong>
                            <ul className="mb-0 mt-1">
                              {email.subjectVariants.map((variant, i) => (
                                <li key={i}>{variant}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="mb-3">
                            <strong>Preview Text:</strong>
                            <p className="mb-0 text-muted">{email.previewText}</p>
                          </div>
                          <div className="mb-3">
                            <strong>Body:</strong>
                            <div
                              className="bg-white p-3 border rounded mt-2"
                              style={{ whiteSpace: "pre-wrap" }}
                            >
                              {email.body}
                            </div>
                          </div>
                          <div className="mb-3">
                            <strong>Call to Action:</strong>
                            <p className="mb-0">{email.callToAction}</p>
                          </div>
                          <div className="mb-3">
                            <strong>Personalization Tags:</strong>
                            {email.personalizationTags.map((tag, i) => (
                              <span key={i} className="badge bg-light-primary text-primary me-1">
                                {tag}
                              </span>
                            ))}
                          </div>
                          <button
                            className="btn btn-sm btn-light-primary"
                            onClick={() => copyToClipboard(email.body)}
                          >
                            <i className="bi bi-clipboard me-1" />
                            Copy Body
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {emailSequences.length === 0 && !isGenerating && (
        <div className="text-center py-10 text-muted">
          <i className="bi bi-envelope fs-1 d-block mb-3" />
          <p>No email sequences generated yet. Choose a type above to get started.</p>
        </div>
      )}
    </div>
  );
};

/**
 * Video Scripts Tab Component
 */
const VideoScriptsTab: React.FC<{
  projectId: string;
  videoScripts: VideoScript[];
  isGenerating: boolean;
  onGenerate: (
    topic: string,
    platform: "youtube" | "tiktok" | "reels" | "explainer",
    duration: "short" | "medium" | "long"
  ) => void;
  onRemove: (index: number) => void;
}> = ({ videoScripts, isGenerating, onGenerate, onRemove }) => {
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState<"youtube" | "tiktok" | "reels" | "explainer">("youtube");
  const [duration, setDuration] = useState<"short" | "medium" | "long">("medium");
  const [expandedScript, setExpandedScript] = useState<number | null>(null);

  const handleGenerate = () => {
    if (topic.trim()) {
      onGenerate(topic, platform, duration);
      setTopic("");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const platformLabels: Record<string, { label: string; icon: string }> = {
    youtube: { label: "YouTube", icon: "bi-youtube" },
    tiktok: { label: "TikTok", icon: "bi-tiktok" },
    reels: { label: "Instagram Reels", icon: "bi-instagram" },
    explainer: { label: "Explainer Video", icon: "bi-play-circle" },
  };

  const formatScript = (script: VideoScript): string => {
    let content = `# ${script.title}\n`;
    content += `**Platform:** ${platformLabels[script.platform]?.label}\n`;
    content += `**Duration:** ${script.duration}\n\n`;
    content += `## Hook\n${script.hook}\n\n`;
    content += `## Script\n\n`;
    script.sections.forEach((section) => {
      content += `### [${section.timestamp}] ${section.type.toUpperCase()}\n`;
      content += `${section.content}\n`;
      if (section.visualNotes) {
        content += `*Visual: ${section.visualNotes}*\n`;
      }
      content += `\n`;
    });
    content += `## Call to Action\n${script.cta}\n\n`;
    content += `## B-Roll Suggestions\n${script.bRollSuggestions.map((b) => `- ${b}`).join("\n")}\n\n`;
    content += `## Thumbnail Ideas\n${script.thumbnailIdeas.map((t) => `- ${t}`).join("\n")}`;
    return content;
  };

  return (
    <div className="p-4">
      {/* Generation Form */}
      <div className="card bg-light-warning border-0 mb-6">
        <div className="card-body">
          <h5 className="fw-bold mb-4">Generate Video Script</h5>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">Video Topic</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., 5 mistakes first-time founders make"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Platform</label>
              <select
                className="form-select"
                value={platform}
                onChange={(e) =>
                  setPlatform(e.target.value as "youtube" | "tiktok" | "reels" | "explainer")
                }
              >
                {Object.entries(platformLabels).map(([value, { label }]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Duration</label>
              <select
                className="form-select"
                value={duration}
                onChange={(e) => setDuration(e.target.value as "short" | "medium" | "long")}
              >
                <option value="short">Short (30-60 sec)</option>
                <option value="medium">Medium (2-5 min)</option>
                <option value="long">Long (8-15 min)</option>
              </select>
            </div>
            <div className="col-12">
              <button
                className="btn btn-warning"
                onClick={handleGenerate}
                disabled={isGenerating || !topic.trim()}
              >
                {isGenerating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-magic me-2" />
                    Generate Video Script
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Generated Scripts */}
      {videoScripts.length > 0 && (
        <div>
          <h5 className="fw-bold mb-4">Generated Scripts ({videoScripts.length})</h5>
          {videoScripts.map((script, index) => (
            <div key={index} className="card mb-4 border">
              <div
                className="card-header d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => setExpandedScript(expandedScript === index ? null : index)}
              >
                <div>
                  <h6 className="mb-0 fw-bold">{script.title}</h6>
                  <small className="text-muted">
                    <i className={`${platformLabels[script.platform]?.icon} me-1`} />
                    {platformLabels[script.platform]?.label} | {script.duration}
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-light-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(formatScript(script));
                    }}
                  >
                    <i className="bi bi-clipboard" />
                  </button>
                  <button
                    className="btn btn-sm btn-light-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(index);
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                  <i
                    className={`bi ${expandedScript === index ? "bi-chevron-up" : "bi-chevron-down"}`}
                  />
                </div>
              </div>
              {expandedScript === index && (
                <div className="card-body">
                  {/* Hook */}
                  <div className="mb-4">
                    <h6 className="fw-bold text-danger">
                      <i className="bi bi-lightning me-1" />
                      Hook
                    </h6>
                    <p className="mb-2">{script.hook}</p>
                    {script.hookVariants.length > 0 && (
                      <div className="ms-3">
                        <small className="text-muted">Alternatives:</small>
                        <ul className="mb-0">
                          {script.hookVariants.map((variant, i) => (
                            <li key={i}>
                              <small>{variant}</small>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Script Sections */}
                  <div className="mb-4">
                    <h6 className="fw-bold">Script</h6>
                    {script.sections.map((section, sIdx) => (
                      <div key={sIdx} className="mb-3 p-3 bg-light rounded">
                        <div className="d-flex justify-content-between mb-2">
                          <span className="badge bg-dark">{section.timestamp}</span>
                          <span className="badge bg-secondary">{section.type}</span>
                        </div>
                        <p className="mb-1" style={{ whiteSpace: "pre-wrap" }}>
                          {section.content}
                        </p>
                        {section.visualNotes && (
                          <small className="text-muted fst-italic">
                            <i className="bi bi-camera-video me-1" />
                            {section.visualNotes}
                          </small>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="mb-4">
                    <h6 className="fw-bold text-success">
                      <i className="bi bi-hand-index me-1" />
                      Call to Action
                    </h6>
                    <p>{script.cta}</p>
                  </div>

                  {/* B-Roll */}
                  <div className="mb-4">
                    <h6 className="fw-bold">B-Roll Suggestions</h6>
                    <ul className="mb-0">
                      {script.bRollSuggestions.map((suggestion, i) => (
                        <li key={i}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Thumbnails */}
                  <div>
                    <h6 className="fw-bold">Thumbnail Ideas</h6>
                    <ul className="mb-0">
                      {script.thumbnailIdeas.map((idea, i) => (
                        <li key={i}>{idea}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {videoScripts.length === 0 && !isGenerating && (
        <div className="text-center py-10 text-muted">
          <i className="bi bi-camera-video fs-1 d-block mb-3" />
          <p>No video scripts generated yet. Enter a topic above to get started.</p>
        </div>
      )}
    </div>
  );
};

/**
 * Main Content Creator Modal
 */
export const ContentCreatorModal: React.FC<ContentCreatorModalProps> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
}) => {
  const {
    isGenerating,
    error,
    blogPosts,
    socialMedia,
    emailSequences,
    videoScripts,
    generateBlogPost,
    generateSocialContent,
    generateEmailSequence,
    generateVideoScript,
    removeBlogPost,
    removeEmailSequence,
    removeVideoScript,
  } = useContentCreator();

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      scrollable
      contentClassName="rounded-4"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold">
          <i className="bi bi-file-earmark-text text-primary me-2" />
          Content Creator
          <span className="text-muted fw-normal fs-6 ms-2">for {projectTitle}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0">
        {error && (
          <div className="alert alert-danger m-4">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}

        <Tabs defaultActiveKey="blog" className="mb-0 px-4 pt-3">
          <Tab
            eventKey="blog"
            title={
              <span>
                <i className="bi bi-file-earmark-text me-1" />
                Blog Posts
              </span>
            }
          >
            <BlogPostTab
              projectId={projectId}
              blogPosts={blogPosts}
              isGenerating={isGenerating}
              onGenerate={(topic, keyword, wordCount) =>
                generateBlogPost(projectId, topic, keyword, wordCount)
              }
              onRemove={removeBlogPost}
            />
          </Tab>
          <Tab
            eventKey="social"
            title={
              <span>
                <i className="bi bi-share me-1" />
                Social Media
              </span>
            }
          >
            <SocialMediaTab
              projectId={projectId}
              socialMedia={socialMedia}
              isGenerating={isGenerating}
              onGenerate={(topic, platforms, postsPerPlatform) =>
                generateSocialContent(projectId, topic, platforms, postsPerPlatform)
              }
            />
          </Tab>
          <Tab
            eventKey="email"
            title={
              <span>
                <i className="bi bi-envelope me-1" />
                Email Sequences
              </span>
            }
          >
            <EmailSequencesTab
              projectId={projectId}
              emailSequences={emailSequences}
              isGenerating={isGenerating}
              onGenerate={(type, count) => generateEmailSequence(projectId, type, count)}
              onRemove={removeEmailSequence}
            />
          </Tab>
          <Tab
            eventKey="video"
            title={
              <span>
                <i className="bi bi-camera-video me-1" />
                Video Scripts
              </span>
            }
          >
            <VideoScriptsTab
              projectId={projectId}
              videoScripts={videoScripts}
              isGenerating={isGenerating}
              onGenerate={(topic, platform, duration) =>
                generateVideoScript(projectId, topic, platform, duration)
              }
              onRemove={removeVideoScript}
            />
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};
